import styles from "../../communication-chat.module.css";
import { useEffect, useRef, useState, useMemo } from "react";
import ContentLoader from "react-content-loader";
import moment from "moment";
import { Edit2, Plus, Copy, ChevronDown, CornerUpLeft } from "react-feather";
import { Popover } from "@mui/material";
import EmojiPicker from "emoji-picker-react";
import IdentityMessageFileViewer from "./identityMessageFileViewer";
import { useAuth0 } from "@auth0/auth0-react";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

// The chat widget (.CommunicationPopupSec.Open) sits at z-index 9991, above
// MUI's default popover layer, so menus opened from inside it need to be higher.
const POPOVER_Z_INDEX = 10000;

const STICK_TO_BOTTOM_THRESHOLD = 120;

// Touch devices open the message menu with a long press (no hover there)
const LONG_PRESS_MS = 450;
const LONG_PRESS_MOVE_TOLERANCE = 10;

const IdentityChatMessages = ({
  messages,
  messageLoader,
  currentUserId,
  resolveAuthorName,
  onStartEdit,
  onStartReply,
  editingMessageSid,
  onToggleReaction,
  onLoadOlderMessages,
  hasMoreOlderMessages,
  isLoadingOlderMessages,
}) => {
  const chatAreaRef = useRef(null);
  const lastMessageSidRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const isFetchingOlderRef = useRef(false);
  // Tracks whether the view is anchored to the bottom, kept up to date by real
  // scroll events. Media loads re-snap only while this is true, so the user is
  // never yanked down while reading history.
  const stickToBottomRef = useRef(true);
  const longPressTimerRef = useRef(null);
  const longPressStartRef = useRef(null);
  // The finger lift that ends a long press also fires a click; swallow it so
  // it doesn't open an image or jump to a quoted message
  const suppressNextClickRef = useRef(false);

  // { sid, anchorEl } when opened from the chevron, { sid, position } from right-click
  const [menu, setMenu] = useState(null);
  const [fullEmojiMenu, setFullEmojiMenu] = useState(null);
  const [copiedSid, setCopiedSid] = useState(null);

  const { isAuthenticated, user } = useAuth0();
  const isSMSChatTeamUpdate =
    isAuthenticated &&
    user["https://propertydek.com/permissions"].indexOf("smschat-team:update") > -1;

  // Group messages by date for the sticky date dividers
  const dateGroups = useMemo(() => {
    const groups = [];
    messages.forEach((msg) => {
      const dateKey = msg.createdAt
        ? moment(msg.createdAt).format("YYYY-MM-DD")
        : "unknown";
      let lastGroup = groups[groups.length - 1];
      if (!lastGroup || lastGroup.dateKey !== dateKey) {
        let label = "Today";
        if (msg.createdAt) {
          const m = moment(msg.createdAt);
          const now = moment();
          if (m.isSame(now, "day")) label = "Today";
          else if (m.isSame(now.clone().subtract(1, "day"), "day"))
            label = "Yesterday";
          else if (m.isSame(now, "year")) label = m.format("MMMM Do");
          else label = m.format("MMMM Do, YYYY");
        }
        lastGroup = { dateKey, dateLabel: label, msgs: [] };
        groups.push(lastGroup);
      }
      lastGroup.msgs.push(msg);
    });
    return groups;
  }, [messages]);

  // New message appended (or first load) — jump to the bottom
  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el) return;

    const newLastSid = messages.length
      ? messages[messages.length - 1].sid
      : null;
    const isAppend = newLastSid !== lastMessageSidRef.current;
    lastMessageSidRef.current = newLastSid;

    // A prepend (older messages loaded) is handled by the scroll-restore effect below
    if (!isAppend) return;
    stickToBottomRef.current = true;
    el.scrollTop = el.scrollHeight;

    const settleTimer = setTimeout(() => {
      el.scrollTop = el.scrollHeight;
    }, 200);
    return () => clearTimeout(settleTimer);
  }, [messages]);

  // Older messages prepended — keep the view on the message the user was reading
  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el || !prevScrollHeightRef.current) return;
    const diff = el.scrollHeight - prevScrollHeightRef.current;
    if (diff > 0) {
      el.scrollTop = el.scrollTop + diff;
    }
    prevScrollHeightRef.current = 0;
  }, [messages]);

  useEffect(() => {
    if (!isLoadingOlderMessages) {
      isFetchingOlderRef.current = false;
    }
  }, [isLoadingOlderMessages]);

  const handleScroll = () => {
    const el = chatAreaRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < STICK_TO_BOTTOM_THRESHOLD;

    if (isFetchingOlderRef.current || !hasMoreOlderMessages) return;
    if (el.scrollTop < 60) {
      isFetchingOlderRef.current = true; // blocks any further calls immediately
      prevScrollHeightRef.current = el.scrollHeight;
      onLoadOlderMessages?.();
    }
  };

  const handleMediaSettled = () => {
    const el = chatAreaRef.current;
    if (!el || !stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  };

  const scrollToMessage = (targetSid) => {
    if (!targetSid) return;
    const targetEl = document.getElementById(`msg-${targetSid}`);
    if (!targetEl) return;
    targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
    const bubbleEl = targetEl.querySelector(`.${styles.MessageBlogCon}`);
    if (bubbleEl) {
      bubbleEl.classList.add(styles.HighlightMessage);
      setTimeout(() => {
        bubbleEl.classList.remove(styles.HighlightMessage);
      }, 1500);
    }
  };

  // Highlight "@Name" for every id in the message's `mentions` attribute
  const renderMessageWithMentions = (text, mentions) => {
    const mentionIds = Array.isArray(mentions) ? mentions : [];
    if (mentionIds.length === 0) return text;

    const escaped = mentionIds
      .map((id) => (id === "all" ? "all" : String(resolveAuthorName(id) ?? "")))
      .filter(Boolean)
      .sort((a, b) => b.length - a.length)
      .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    if (escaped.length === 0) return text;

    const pattern = new RegExp(`(@(?:${escaped.join("|")}))`, "g");
    return text.split(pattern).map((part, idx) =>
      idx % 2 === 1 ? (
        <span key={idx} className={styles.WhatsappMentionTag}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const getReplyPreviewLabel = (replyTo) => {
    if (replyTo.previewText) return replyTo.previewText;
    if (replyTo.mediaType?.startsWith("image/")) return "📷 Photo";
    if (replyTo.mediaType?.startsWith("video/")) return "🎥 Video";
    if (replyTo.mediaType?.startsWith("audio/")) return "🎤 Voice message";
    if (replyTo.mediaType) return "📎 Attachment";
    return "Message";
  };

  const openMenuFromButton = (e, sid) => {
    e.stopPropagation();
    setMenu({ sid, anchorEl: e.currentTarget });
  };

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    longPressStartRef.current = null;
  };

  useEffect(() => clearLongPressTimer, []);

  // Right-click on desktop. Android Chrome also fires this on a long press,
  // so it's de-duplicated against the long-press timer below.
  const openMenuFromContextMenu = (e, sid) => {
    e.preventDefault();
    if (longPressTimerRef.current) {
      clearLongPressTimer();
      suppressNextClickRef.current = true;
    }
    if (menu?.sid === sid) return;
    setMenu({ sid, position: { top: e.clientY, left: e.clientX } });
  };

  // Per-event check: a touchscreen laptop gets long-press for fingers and
  // the hover chevron / right-click for the mouse
  const handleBubblePointerDown = (e, sid) => {
    suppressNextClickRef.current = false;
    if (e.pointerType !== "touch") return;
    const start = { x: e.clientX, y: e.clientY };
    clearLongPressTimer();
    longPressStartRef.current = start;
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      suppressNextClickRef.current = true;
      setMenu({ sid, position: { top: start.y, left: start.x } });
    }, LONG_PRESS_MS);
  };

  // Moving the finger means the user is scrolling, not long-pressing
  const handleBubblePointerMove = (e) => {
    if (!longPressStartRef.current) return;
    const dx = Math.abs(e.clientX - longPressStartRef.current.x);
    const dy = Math.abs(e.clientY - longPressStartRef.current.y);
    if (dx > LONG_PRESS_MOVE_TOLERANCE || dy > LONG_PRESS_MOVE_TOLERANCE) {
      clearLongPressTimer();
    }
  };

  const handleBubbleClickCapture = (e) => {
    if (!suppressNextClickRef.current) return;
    suppressNextClickRef.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  const getPopoverAnchorProps = (target) =>
    target?.anchorEl
      ? { anchorEl: target.anchorEl, anchorReference: "anchorEl" }
      : { anchorPosition: target?.position, anchorReference: "anchorPosition" };

  const handleReactionClick = (msg, emoji) => {
    setMenu(null);
    setFullEmojiMenu(null);
    onToggleReaction(msg, emoji);
  };

  const handleCopyMessage = async (msg) => {
    try {
      await navigator.clipboard.writeText(msg.message || "");
      setCopiedSid(msg.sid);
      setTimeout(() => setCopiedSid(null), 1200);
    } catch (err) {
      console.log("[IDENTITY CHAT] copy failed", err);
    }
    setMenu(null);
  };

  const menuMessage = menu ? messages.find((m) => m.sid === menu.sid) : null;
  const fullEmojiMessage = fullEmojiMenu
    ? messages.find((m) => m.sid === fullEmojiMenu.sid)
    : null;

  const renderMenuActions = (msg) => {
    const isMine = String(msg.author) === String(currentUserId);
    const isCallLog = msg.message?.startsWith("📞");
    const canEditThis =
      isMine &&
      !!msg.message &&
      !isCallLog &&
      msg.sid !== editingMessageSid &&
      isSMSChatTeamUpdate;

    return (
      <div className={styles.WhatsappMenuList}>
        <button
          type="button"
          className={styles.WhatsappMenuItem}
          onClick={() => {
            onStartReply(msg);
            setMenu(null);
          }}
        >
          <CornerUpLeft size={16} />
          <span>Reply</span>
        </button>
        {canEditThis && (
          <button
            type="button"
            className={styles.WhatsappMenuItem}
            onClick={() => {
              onStartEdit(msg);
              setMenu(null);
            }}
          >
            <Edit2 size={16} />
            <span>Edit</span>
          </button>
        )}
        {msg.message && (
          <button
            type="button"
            className={styles.WhatsappMenuItem}
            onClick={() => handleCopyMessage(msg)}
          >
            <Copy size={16} />
            <span>{copiedSid === msg.sid ? "Copied!" : "Copy"}</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {isLoadingOlderMessages && ( // NEW
        <div
          style={{
            textAlign: "center",
            padding: "8px 0",
            fontSize: "12px",
            color: "#343434",
          }}
        >
          Loading earlier messages…
        </div>
      )}
      {messageLoader && (
        <div className={`${styles.ChatBoxContentLoader}`}>
          <ContentLoader width={400} height={300}>
            <rect x="27" y="40" rx="3" ry="3" width="260" height="10"></rect>
            <rect x="27" y="60" rx="3" ry="3" width="240" height="10"></rect>
            <rect x="27" y="80" rx="3" ry="3" width="180" height="10"></rect>
          </ContentLoader>
        </div>
      )}

      <div
        ref={chatAreaRef}
        className={`${styles.ChatViewSec} ${styles.IdentityChatViewSec}`}
        style={{ overflowY: "scroll" }}
        onScroll={handleScroll}
      >
        {dateGroups.map((group) => (
          <div key={group.dateKey} className={styles.WhatsappDateGroupSection}>
            <div className={styles.WhatsappDateDividerRow}>
              <span className={styles.WhatsappDateDividerPill}>
                {group.dateLabel}
              </span>
            </div>

            {group.msgs.map((msg, i) => {
              const isMine = String(msg.author) === String(currentUserId);
              const wasEdited =
                msg.updatedAt &&
                msg.createdAt &&
                new Date(msg.updatedAt).getTime() !==
                new Date(msg.createdAt).getTime();
              const isBeingEdited = msg.sid === editingMessageSid;
              const replyTo = msg._message?.attributes?.replyTo;
              const reactionEntries = Object.entries(msg.reactions || {}).filter(
                ([, ids]) => ids.length > 0
              );

              return (
                <div
                  id={`msg-${msg.sid}`}
                  className={
                    isMine
                      ? `${styles.MessageBlog} ${styles.Receiver}`
                      : `${styles.MessageBlog}`
                  }
                  key={msg.sid || i}
                >
                  <div className={`${styles.MessageBlogInner}`}>
                    <div
                      className={`${styles.MessageBlogCon} ${isMine ? styles.OutgoingBubble : styles.IncomingBubble
                        }`}
                      style={isBeingEdited ? { opacity: 0.5 } : undefined}
                      onContextMenu={(e) => openMenuFromContextMenu(e, msg.sid)}
                      onPointerDown={(e) => handleBubblePointerDown(e, msg.sid)}
                      onPointerMove={handleBubblePointerMove}
                      onPointerUp={clearLongPressTimer}
                      onPointerLeave={clearLongPressTimer}
                      onPointerCancel={clearLongPressTimer}
                      onClickCapture={handleBubbleClickCapture}
                    >
                      <div className={styles.WhatsappHoverActions}>
                        <button
                          type="button"
                          className={styles.WhatsappHoverBtn}
                          onClick={(e) => openMenuFromButton(e, msg.sid)}
                          title="Message actions"
                        >
                          <ChevronDown size={15} />
                        </button>
                      </div>

                      {!isMine && (
                        <p className={`${styles.MessageName}`}>
                          {resolveAuthorName(msg.author)}
                          {isBeingEdited && (
                            <span className={styles.WhatsappEditingLabel}>
                              Editing…
                            </span>
                          )}
                        </p>
                      )}

                      {isMine && isBeingEdited && (
                        <p className={`${styles.MessageName}`}>
                          <span className={styles.WhatsappEditingLabel}>
                            Editing message…
                          </span>
                        </p>
                      )}

                      {replyTo && (
                        <div
                          className={`${styles.WhatsappReplyQuoteBox} ${isMine ? styles.MineQuote : styles.OtherQuote
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            scrollToMessage(replyTo.messageSid);
                          }}
                        >
                          <div className={styles.WhatsappReplyAuthor}>
                            {String(replyTo.senderUserId) === String(currentUserId)
                              ? "You"
                              : resolveAuthorName(replyTo.senderUserId)}
                          </div>
                          <div className={styles.WhatsappReplyText}>
                            {getReplyPreviewLabel(replyTo)}
                          </div>
                        </div>
                      )}

                      {msg.message && (
                        <div className={`${styles.MessageDesMain}`}>
                          <p className={`${styles.MessageDes}`}>
                            {renderMessageWithMentions(
                              msg.message,
                              msg._message?.attributes?.mentions
                            )}
                          </p>
                        </div>
                      )}

                      {msg.media && msg.media.length > 0 && (
                        <div className={`${styles.MessageFileSec}`}>
                          <IdentityMessageFileViewer
                            media={msg.media}
                            onMediaLoad={handleMediaSettled}
                          />
                        </div>
                      )}

                      {reactionEntries.length > 0 && (
                        <div className={styles.WhatsappReactionsBadgeRow}>
                          {reactionEntries.map(([emoji, ids]) => {
                            const mine = ids.includes(String(currentUserId));
                            return (
                              <span
                                key={emoji}
                                onClick={() => onToggleReaction(msg, emoji)}
                                className={`${styles.WhatsappReactionBadge} ${mine ? styles.MineReaction : ""
                                  }`}
                              >
                                {emoji} {ids.length}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      <div className={styles.WhatsappMessageFooter}>
                        <span
                          className={styles.WhatsappTimestamp}
                          title={
                            msg.createdAt
                              ? moment(msg.createdAt).format(
                                "MMMM Do, YYYY [at] h:mm A"
                              )
                              : ""
                          }
                        >
                          {moment(msg.createdAt).format("h:mm A")}
                          {wasEdited && " (edited)"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {!messageLoader && messages.length === 0 && (
          <div className={`${styles.ChatBoxnoDataSec}`}>
            <img src="/static/img/chatIcon.png" alt="logo" />
            <p>Start a New Conversation</p>
          </div>
        )}
      </div>

      {/* Unified message menu: quick reactions + actions */}
      <Popover
        open={!!menuMessage}
        onClose={() => setMenu(null)}
        {...getPopoverAnchorProps(menu)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: POPOVER_Z_INDEX }}
        PaperProps={{ className: styles.WhatsappMessageMenuPaper }}
      >
        {menuMessage && (
          <div className={styles.WhatsappPopoverContent}>
            <div className={styles.WhatsappQuickReactionsRow}>
              {QUICK_REACTIONS.map((emoji) => (
                <span
                  key={emoji}
                  className={styles.WhatsappQuickReactionItem}
                  onClick={() => handleReactionClick(menuMessage, emoji)}
                >
                  {emoji}
                </span>
              ))}
              <span
                className={styles.WhatsappQuickReactionAdd}
                onClick={() => {
                  setFullEmojiMenu(menu);
                  setMenu(null);
                }}
              >
                <Plus size={14} />
              </span>
            </div>

            <div className={styles.WhatsappMenuDivider} />

            {renderMenuActions(menuMessage)}
          </div>
        )}
      </Popover>

      {/* Full emoji picker for reactions */}
      <Popover
        open={!!fullEmojiMessage}
        onClose={() => setFullEmojiMenu(null)}
        {...getPopoverAnchorProps(fullEmojiMenu)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: POPOVER_Z_INDEX }}
      >
        {fullEmojiMessage && (
          <div className={styles.WhatsappEmojiPickerContainer}>
            <EmojiPicker
              onEmojiClick={(emojiData) =>
                handleReactionClick(fullEmojiMessage, emojiData.emoji)
              }
              height={350}
              width={300}
              previewConfig={{ showPreview: false }}
              autoFocusSearch={false}
              searchPlaceHolder="Search reaction emoji"
            />
          </div>
        )}
      </Popover>
    </>
  );
};

export default IdentityChatMessages;
